import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Table,
  Tag,
  Space,
  Typography,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  BarcodeOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  InboxOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import Header from "../../components/Header";
import Layout from "../../layout/Layout";

import fetchData from "../../functions/fetchData";
import { deleteItem, deleteBulkItems } from "../../functions/delateData";
import { isTokenExpired, logout } from "../../utility/auth";
import NewItemPriceModal from "../../components/Modals/NewItemPriceModal";
import BarcodeModal from "../../components/Modals/BarcodeModal";
import EditItemModal from "../../components/Modals/EditItemModal";

// Import libraries for export functionality
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text } = Typography;

export default function NewItem() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reload, setReload] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const AddNewItemPriceButton = () => {
    setShowModal(true);
  };

  const GenerateBarcodeButton = () => {
    setShowBarcodeModal(true);
  };

  const triggerReload = () => {
    setReload((prev) => !prev);
  };

  // Check if token is expired using the auth utility
  const checkTokenExpiration = () => {
    if (isTokenExpired()) {
      toast.error('Your session has expired. Please log in again.', {
        position: "top-center",
        autoClose: 3000,
      });
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 1000);
      return false;
    }
    return true;
  };

  // Export functions
  const exportToExcel = () => {
    try {
      const exportData = filteredData.map(item => ({
        'ID': item.id,
        'Item Code': item.item_code || '',
        'Barcode': item.barcode || '',
        'Item Name': item.iname || '',
        'Brand': item.brand || '',
        'Unit': item.unit || '',
        'Supplier': item.supplier_name || '',
        'MRP': item.mrp || '0',
        'Selling Price': item.offerprice || '0',
        'Tax (%)': item.tax || '0',
        'Min Stock': item.min_stock || '0',
        'Reorder Qty': item.reorder_quantity || '0',
        'Location': item.location || '',
        'HSN Code': item.hsn_code || '',
        'Warranty (Months)': item.warranty_months || '',
        'Shelf Life (Days)': item.shelf_life_days || '',
        'Stockable': item.isstockable ? 'Yes' : 'No',
        'Description': item.description || ''
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Items");

      const filename = `items_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success('Excel file exported successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel file', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.text('Items Report', 14, 22);

      doc.setFontSize(10);
      doc.text(`Generated on: ${format(new Date(), "dd/MM/yyyy HH:mm:ss")}`, 14, 32);

      const tableData = filteredData.map(item => [
        item.id,
        item.item_code || '',
        item.iname || '',
        item.brand || '',
        item.unit || '',
        `฿${item.mrp || '0'}`,
        `฿${item.offerprice || '0'}`,
        item.min_stock || '0',
        item.supplier_name || ''
      ]);

      doc.autoTable({
        head: [['ID', 'Code', 'Item Name', 'Brand', 'Unit', 'MRP', 'Selling', 'Stock', 'Supplier']],
        body: tableData,
        startY: 40,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [13, 148, 136],
          textColor: 255
        },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 22 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
          4: { cellWidth: 15 },
          5: { cellWidth: 22 },
          6: { cellWidth: 22 },
          7: { cellWidth: 15 },
          8: { cellWidth: 30 }
        }
      });

      const filename = `items_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.pdf`;
      doc.save(filename);

      toast.success('PDF file exported successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF file', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const printTable = () => {
    try {
      const printWindow = window.open('', '_blank');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Items Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            h1 {
              color: #0d9488;
              text-align: center;
              margin-bottom: 10px;
            }
            .header-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #0d9488;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f2f2f2;
            }
            .currency {
              text-align: right;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Items Report</h1>
          <div class="header-info">
            <p>Generated on: ${format(new Date(), "dd/MM/yyyy HH:mm:ss")}</p>
            <p>Total Items: ${data.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Brand</th>
                <th>Unit</th>
                <th>MRP</th>
                <th>Selling Price</th>
                <th>Min Stock</th>
                <th>Supplier</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.id}</td>
                  <td>${item.item_code || '-'}</td>
                  <td>${item.iname || ''}</td>
                  <td>${item.brand || '-'}</td>
                  <td>${item.unit || ''}</td>
                  <td class="currency">฿${parseFloat(item.mrp || 0).toFixed(2)}</td>
                  <td class="currency">฿${parseFloat(item.offerprice || 0).toFixed(2)}</td>
                  <td>${item.min_stock || '0'}</td>
                  <td>${item.supplier_name || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };

      toast.success('Print dialog opened successfully!', {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open print dialog', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select items to delete', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const result = await deleteBulkItems(selectedItems);

      if (result.success) {
        toast.success(`${result.affectedRows} item(s) deleted successfully!`, {
          position: "top-right",
          autoClose: 3000,
        });

        setSelectedItems([]);
        triggerReload();
      } else {
        toast.error(`Failed to delete items: ${result.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Bulk delete error:', error);

      if (error.status === 401 || error.status === 403) {
        toast.error('Your session has expired. Please log in again.', {
          position: "top-center",
          autoClose: 3000,
        });
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 1000);
        return;
      }

      toast.error(`Error deleting items: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = async (itemId) => {
    if (!checkTokenExpiration()) {
      return;
    }

    try {
      const result = await deleteItem(itemId);

      if (result.success) {
        toast.success('Item deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
        });

        triggerReload();
      } else {
        toast.error(`Failed to delete item: ${result.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Delete item error:', error);

      if (error.status === 401 || error.status === 403) {
        toast.error('Your session has expired. Please log in again.', {
          position: "top-center",
          autoClose: 3000,
        });
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 1000);
        return;
      }

      toast.error(`Error deleting item: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    const fetchAndSetData = async () => {
      if (!checkTokenExpiration()) {
        return;
      }

      try {
        const items = await fetchData("items", setData, "id", {});
        setData(items);
        setOriginalData(items);
        setFilteredData(items);
      } catch (error) {
        console.error("Error in useEffect:", error);

        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          toast.error('Your session has expired. Please log in again.', {
            position: "top-center",
            autoClose: 3000,
          });
          setTimeout(() => {
            logout();
            navigate('/login');
          }, 1000);
        } else {
          toast.error('Failed to fetch items data', {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    };

    fetchAndSetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  // Filter function
  const applyFilters = () => {
    let filtered = originalData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.iname && item.iname.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower)) ||
        (item.id && item.id.toString().includes(searchLower))
      );
    }

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilteredData(originalData);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, originalData]);

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, sorter: (a, b) => a.id - b.id, render: (v) => <Text strong>{v}</Text> },
    { title: "Item Code", dataIndex: "item_code", key: "item_code", sorter: (a, b) => (a.item_code || '').localeCompare(b.item_code || ''), render: (v) => v || '-' },
    { title: "Item Name", dataIndex: "iname", key: "iname", sorter: (a, b) => (a.iname || '').localeCompare(b.iname || ''), render: (v) => v || 'N/A' },
    { title: "Brand", dataIndex: "brand", key: "brand", sorter: (a, b) => (a.brand || '').localeCompare(b.brand || ''), render: (v) => v || '-' },
    { title: "Unit", dataIndex: "unit", key: "unit", render: (v) => v || 'N/A' },
    { title: "MRP", dataIndex: "mrp", key: "mrp", sorter: (a, b) => (a.mrp || 0) - (b.mrp || 0), render: (v) => `฿${v ? parseFloat(v).toFixed(2) : '0.00'}` },
    { title: "Selling Price", dataIndex: "offerprice", key: "offerprice", sorter: (a, b) => (a.offerprice || 0) - (b.offerprice || 0), render: (v) => `฿${v ? parseFloat(v).toFixed(2) : '0.00'}` },
    {
      title: "Min Stock",
      dataIndex: "min_stock",
      key: "min_stock",
      sorter: (a, b) => (a.min_stock || 0) - (b.min_stock || 0),
      render: (v) => <Tag color={v && parseInt(v) > 0 ? "blue" : "default"}>{v || '0'}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, item) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditClick(item)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this item?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteItem(item.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Layout>
        <Header title="Item Details" />
        <ToastContainer />

        <Card
          style={{ marginBottom: 16, borderRadius: 12, background: '#f0fdfa', border: '1px solid #99f6e4' }}
          styles={{ body: { padding: 16 } }}
        >
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col xs={24} md={8}>
              <Input
                allowClear
                size="middle"
                prefix={<SearchOutlined style={{ color: '#0d9488' }} />}
                placeholder="Search by item name, description, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={clearFilters}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredData ? filteredData.length : 0} of {originalData ? originalData.length : 0} items
              </Text>
            </Col>

            <Col xs={24} md={16} style={{ textAlign: 'right' }}>
              <Space wrap>
                {selectedItems.length > 0 && (
                  <Popconfirm
                    title={`Delete ${selectedItems.length} selected item(s)?`}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={handleBulkDelete}
                  >
                    <Button danger icon={<CloseCircleOutlined />}>
                      Delete ({selectedItems.length})
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={exportToExcel}
                  disabled={!filteredData || filteredData.length === 0}
                >
                  Excel
                </Button>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={exportToPDF}
                  disabled={!filteredData || filteredData.length === 0}
                >
                  PDF
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={printTable}
                  disabled={!filteredData || filteredData.length === 0}
                >
                  Print
                </Button>
                <Button icon={<BarcodeOutlined />} onClick={GenerateBarcodeButton}>
                  Generate Barcode
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={AddNewItemPriceButton}>
                  Add New Item
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredData}
            rowSelection={{
              selectedRowKeys: selectedItems,
              onChange: (keys) => setSelectedItems(keys),
            }}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 25, 50, 100] }}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <div style={{ padding: 40 }}>
                  <InboxOutlined style={{ fontSize: 32, color: '#94a3b8' }} />
                  <div style={{ marginTop: 8, color: '#64748b' }}>No items available</div>
                </div>
              ),
            }}
          />
        </Card>

        <NewItemPriceModal
          isOpen={showModal}
          onItemAdded={triggerReload}
          onClose={() => setShowModal(false)}
        />
        <BarcodeModal
          isOpen={showBarcodeModal}
          items={data}
          onClose={() => setShowBarcodeModal(false)}
        />
        <EditItemModal
          isOpen={showEditModal}
          item={selectedItem}
          onItemUpdated={triggerReload}
          onClose={handleEditModalClose}
        />
      </Layout>
    </>
  );
}
